import React from 'react';
import { createPortal } from 'react-dom';
import { useRecordDockState } from './record-dock.hooks';
import { LiveWaveform } from './line-waveform';
import {
  DockWrapper,
  DockHandleWrapper,
  DockHandle,
  RecordingDot,
  RecordingHeader,
  RecordingTitle,
  RecordingTimer,
  RecordingMiddle,
  DockLabel,
  DockIcon,
  DockItem,
  RecordingBottom,
  RecordingWrapper,
  RerecordConfirmation,
  DiscardAndReRecordTitleSpan,
  DiscardAndReRecordDescriptionSpan,
  CTAWrapper,
  CancelButton,
  ReRecordButton,
  ModalOverlay,
  ModalContent,
  DiscardButton,
  TitleAndCloseWrapper,
} from './record-dock.styled';

interface RecordDockProps {
  onClose?: () => void;
  onSend?: (audioBlob: Blob, files?: File[], transcript?: string) => void;
  animateInOnMount?: boolean;
}

export type RecordDockHandle = {
  requestClose: (onConfirmed?: () => void) => void;
};

export const RecordDock = React.forwardRef<RecordDockHandle, RecordDockProps>(({
  onClose,
  onSend,
  animateInOnMount = false,
}, ref) => {
  const {
    recordingState,
    recordingComplete,
    pauseRecording,
    resumeRecording,
    handleStopAndSend,
    handleReRecord,
    cancelRecording,
    formatTime,
    maxRecordingDuration,
    getTimerColor,
  } = useRecordDockState(onSend, onClose);

  const [showReRecordModal, setShowReRecordModal] = React.useState(false);
  const [showCloseModal, setShowCloseModal] = React.useState(false);
  const [waveformResetTrigger, setWaveformResetTrigger] = React.useState(0);
  const [isAnimatingOut, setIsAnimatingOut] = React.useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = React.useState(animateInOnMount);
  const [dragOffsetY, setDragOffsetY] = React.useState(0);
  const [isSnappingBack, setIsSnappingBack] = React.useState(false);
  const [isSlidingOut, setIsSlidingOut] = React.useState(false);
  const postConfirmCallbackRef = React.useRef<(() => void) | null>(null);
  const dragStartY = React.useRef<number | null>(null);
  const isDragging = React.useRef(false);

  const DRAG_THRESHOLD = 80;

  const handleDragStart = (clientY: number) => {
    dragStartY.current = clientY;
    isDragging.current = false;
  };

  const handleDragMove = (clientY: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY - dragStartY.current;
    if (delta > 5) isDragging.current = true;
    setDragOffsetY(Math.max(0, delta));
  };

  const handleDragEnd = (clientY?: number) => {
    if (dragStartY.current === null) return;
    const delta = clientY != null ? clientY - dragStartY.current : 0;
    const offset = Math.max(0, delta);
    dragStartY.current = null;
    if (offset >= DRAG_THRESHOLD) {
      if (recordingState.isRecording || recordingState.isPaused) {
        setDragOffsetY(0);
        handleClose();
      } else {
        setIsSlidingOut(true);
        setDragOffsetY(999);
        cancelRecording();
        onClose?.();
      }
    } else if (offset > 0) {
      setIsSnappingBack(true);
      setDragOffsetY(0);
      setTimeout(() => setIsSnappingBack(false), 200);
    }
  };

  React.useEffect(() => {
    if (animateInOnMount) {
      const timer = setTimeout(() => setIsAnimatingIn(false), 300);
      return () => clearTimeout(timer);
    }
    return;
  }, [animateInOnMount]);

  const onStopClick = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      handleStopAndSend();
      onClose?.();
    }, 300);
  };

  const handleClose = () => {
    if (recordingState.isRecording && !recordingState.isPaused) {
      // pause and ask
      pauseRecording();
      setShowCloseModal(true);
    } else if (recordingState.isPaused) {
      setShowCloseModal(true);
    } else {
      // not recording, close immediately
      setIsAnimatingOut(true);
      setTimeout(() => {
        cancelRecording();
        onClose?.();
      }, 300);
    }
  };

  React.useImperativeHandle(ref, () => ({
    requestClose: (onConfirmed?: () => void) => {
      postConfirmCallbackRef.current = onConfirmed ?? null;
      handleClose();
    },
  }));

  return (
    <>
      <DockWrapper
        isAnimatingOut={isAnimatingOut}
        isAnimatingIn={isAnimatingIn}
        style={dragOffsetY > 0 ? {
          transform: `translateY(${dragOffsetY}px)`,
          opacity: isSlidingOut ? 0 : Math.max(0.3, 1 - dragOffsetY / 200),
          transition: isSlidingOut ? 'transform 0.2s ease-in, opacity 0.2s ease-in' : 'none',
        } : isSnappingBack ? {
          transform: 'translateY(0px)',
          opacity: 1,
          transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
        } : undefined}
      >
        <DockHandleWrapper
              onTouchStart={(e) => handleDragStart(e.touches[0].clientY)}
              onTouchMove={(e) => handleDragMove(e.touches[0].clientY)}
              onTouchEnd={(e) => handleDragEnd(e.changedTouches[0]?.clientY)}
              onMouseDown={(e) => handleDragStart(e.clientY)}
              onMouseMove={(e) => { if (e.buttons === 1) handleDragMove(e.clientY); }}
              onMouseUp={(e) => handleDragEnd(e.clientY)}
              onMouseLeave={(e) => { if (dragStartY.current !== null) handleDragEnd(e.clientY); }}
            >
              <DockHandle onClick={() => { if (!isDragging.current) handleClose(); }} style={{ cursor: 'grab' }} />
            </DockHandleWrapper>

            <RecordingWrapper>
              <RecordingHeader>
                <RecordingDot />
                <RecordingTitle>Recording</RecordingTitle>
              </RecordingHeader>
              <RecordingTimer>
                <span style={{ color: getTimerColor() }}>
                  {formatTime(recordingState.recordingTime)}
                </span>
                <span style={{ color: "#8F95A3" }}> / {formatTime(maxRecordingDuration)}</span>
              </RecordingTimer>
            </RecordingWrapper>

            <RecordingMiddle>
              <img
                src="/cancel-voice.svg"
                alt="Cancel"
                onClick={handleClose}
                style={{ cursor: 'pointer' }}
              />
              <LiveWaveform
                barWidth={2.8}
                minHeight={2}
                maxHeight={45}
                speed={1}
                amplitudeBoost={1}
                paused={recordingState.isPaused}
                resetTrigger={waveformResetTrigger}
                recordingActive={recordingState.isRecording && !recordingComplete && !recordingState.isPaused}
              />
            </RecordingMiddle>

        <RecordingBottom>
          {recordingState.isPaused ? (
            <DockItem type="button" onClick={resumeRecording}>
              <DockIcon src="/icons/onboarding/question/resume.svg" alt="Resume" />
              <DockLabel>Resume</DockLabel>
            </DockItem>
          ) : (
            <DockItem type="button" onClick={pauseRecording}>
              <DockIcon src="/icons/onboarding/question/pause.svg" alt="Pause" />
              <DockLabel>Pause</DockLabel>
            </DockItem>
          )}

          <DockItem type="button" onClick={onStopClick}>
            <DockIcon src="/icons/onboarding/question/stop-button.svg" alt="Stop" />
          </DockItem>

          <DockItem
            type="button"
            onClick={() => {
              pauseRecording();
              setShowReRecordModal(true);
            }}
          >
            <DockIcon src="/icons/onboarding/question/re-record.svg" alt="Re-record" />
            <DockLabel>Re-record</DockLabel>
          </DockItem>
        </RecordingBottom>

      </DockWrapper>

      {/* Re-record modal — portalled to body to escape stacking context */}
      {createPortal(
        <ModalOverlay visible={showReRecordModal} onClick={() => setShowReRecordModal(false)}>
          <ModalContent visible={showReRecordModal}>
            <RerecordConfirmation className={showReRecordModal ? 'visible' : ''}>
              <DiscardAndReRecordTitleSpan>Discard and re-record?</DiscardAndReRecordTitleSpan>
              <DiscardAndReRecordDescriptionSpan>
                You&apos;ll begin again. Just remember it doesn&apos;t have to be perfect. Stream of
                consciousness is just fine.
              </DiscardAndReRecordDescriptionSpan>

              <CTAWrapper>
                <CancelButton onClick={() => setShowReRecordModal(false)}>Cancel</CancelButton>

                <ReRecordButton
                  onClick={() => {
                    setShowReRecordModal(false);
                    setWaveformResetTrigger((prev) => prev + 1);
                    handleReRecord();
                  }}
                >
                  Re-record
                </ReRecordButton>
              </CTAWrapper>
            </RerecordConfirmation>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}

      {/* Close/discard modal — portalled to body to escape stacking context */}
      {createPortal(
        <ModalOverlay
          visible={showCloseModal}
          onClick={() => {
            setShowCloseModal(false);
            if (recordingState.isPaused) {
              resumeRecording();
            }
          }}
        >
          <ModalContent visible={showCloseModal}>
            <RerecordConfirmation className={showCloseModal ? 'visible' : ''}>
              <TitleAndCloseWrapper>
                <DiscardAndReRecordTitleSpan>Discard your recording?</DiscardAndReRecordTitleSpan>
              </TitleAndCloseWrapper>

              <DiscardAndReRecordDescriptionSpan>
                You will lose your progress. You can start again anytime.
              </DiscardAndReRecordDescriptionSpan>

              <CTAWrapper style={{ gap: '24px' }}>
                <DiscardButton
                  onClick={() => {
                    setShowCloseModal(false);
                    if (recordingState.isPaused) {
                      resumeRecording();
                    }
                  }}
                >
                  Continue
                </DiscardButton>

                <ReRecordButton
                  onClick={() => {
                    setShowCloseModal(false);
                    setIsAnimatingOut(true);

                    setTimeout(() => {
                      cancelRecording();
                      onClose?.();
                      postConfirmCallbackRef.current?.();
                      postConfirmCallbackRef.current = null;
                    }, 300);
                  }}
                >
                  Discard
                </ReRecordButton>
              </CTAWrapper>
            </RerecordConfirmation>
          </ModalContent>
        </ModalOverlay>,
        document.body
      )}
    </>
  );
});
